import type {
  SectionTenAssessmentV2,
  SectionTenClientDraftHistoryEntry,
  SectionTenClientDraftV2,
  SectionTenSourceSnapshot,
  SectionTenWorkflow,
  SectionTenWorkAreaId,
} from "./domain";
import { prepareSectionTenV2ClientDraft } from "./section10-recommendations";
import {
  linkSavedSectionTenDraft,
  makeSectionTenConfirmationLineage,
  migrateSectionTenLineage,
  sectionTenClientDraftIdentity,
} from "./section10-lineage";

export const SECTION_TEN_CLIENT_FIELDS={
  overview:"overview",
  deliveryOptions:"deliveryOptions",
  nextDecision:"nextDecision",
  evidenceLimitations:"evidenceLimitations",
} as const;

export const sectionTenAreaSummaryKey=(areaId:SectionTenWorkAreaId)=>`workArea:${areaId}:summary`;
export const sectionTenPointKey=(areaId:SectionTenWorkAreaId,index:number)=>`workArea:${areaId}:point:${index}`;

const cloneDraft=(draft:SectionTenClientDraftV2):SectionTenClientDraftV2=>({
  ...draft,
  workAreas:draft.workAreas.map(area=>({...area,points:[...area.points]})),
});

const checksum=(value:string)=>{
  let result=2166136261;
  for(let index=0;index<value.length;index+=1){
    result^=value.charCodeAt(index);
    result=Math.imul(result,16777619);
  }
  return (result>>>0).toString(16).padStart(8,"0");
};

export const sectionTenDraftIdentity=(draft:SectionTenClientDraftV2)=>
  sectionTenClientDraftIdentity(draft,draft.sourceAssessmentConfirmationId);

export function inferSectionTenManualClientFields(draft:SectionTenClientDraftV2,assessment?:SectionTenAssessmentV2,confirmedAt?:string){
  if(!assessment||!confirmedAt)return[];
  const generated=prepareSectionTenV2ClientDraft(assessment,confirmedAt),keys:string[]=[];
  if(draft.overview!==generated.overview)keys.push(SECTION_TEN_CLIENT_FIELDS.overview);
  if(draft.deliveryOptions!==generated.deliveryOptions)keys.push(SECTION_TEN_CLIENT_FIELDS.deliveryOptions);
  if(draft.nextDecision!==generated.nextDecision)keys.push(SECTION_TEN_CLIENT_FIELDS.nextDecision);
  if(draft.evidenceLimitations!==generated.evidenceLimitations)keys.push(SECTION_TEN_CLIENT_FIELDS.evidenceLimitations);
  draft.workAreas.forEach(area=>{
    const generatedArea=generated.workAreas.find(item=>item.id===area.id);
    if(!generatedArea||area.summary!==generatedArea.summary)keys.push(sectionTenAreaSummaryKey(area.id));
    area.points.forEach((point,index)=>{
      if(!generatedArea||point!==generatedArea.points[index])keys.push(sectionTenPointKey(area.id,index));
    });
  });
  return keys;
}

export function sectionTenDraftHistoryEntry(
  draft:SectionTenClientDraftV2,
  options:{identity?:string;updatedAt?:string;reason:string;manualFieldKeys?:string[];capturedAt:string;capturedBy?:string},
):SectionTenClientDraftHistoryEntry{
  const identity=options.identity??sectionTenDraftIdentity(draft);
  return{
    id:`${identity}-${checksum(`${options.reason}|${options.capturedAt}`)}`,
    draftIdentity:identity,
    draft:cloneDraft(draft),
    createdAt:draft.generatedAt,
    updatedAt:options.updatedAt??draft.generatedAt,
    sourceAssessmentConfirmedAt:draft.sourceAssessmentConfirmedAt,
    replacementReason:options.reason,
    manuallyEditedFieldKeys:[...new Set(options.manualFieldKeys??[])],
    capturedAt:options.capturedAt,
    capturedBy:options.capturedBy,
    sourceAssessmentConfirmationId:draft.sourceAssessmentConfirmationId,
  };
}

export function preserveSectionTenClientDraft(
  workflow:SectionTenWorkflow,
  reason:string,
  capturedAt:string,
  capturedBy="Demo Operator",
):SectionTenWorkflow{
  const draft=workflow.v2ClientDraft;
  if(!draft)return workflow;
  const entry=sectionTenDraftHistoryEntry(draft,{
    identity:workflow.v2ClientDraftIdentity,
    updatedAt:workflow.v2ClientDraftUpdatedAt??workflow.clientUpdatedAt,
    reason,
    manualFieldKeys:workflow.v2ClientDraftManualEditFieldKeys,
    capturedAt,
    capturedBy,
  });
  const history=workflow.v2ClientDraftHistory??[];
  return history.some(item=>item.id===entry.id)?workflow:{...workflow,v2ClientDraftHistory:[...history,entry]};
}

export function migrateSectionTenV2Recovery(workflow:SectionTenWorkflow):SectionTenWorkflow{
  if(workflow.redesignVersion!==2)return migrateSectionTenLineage(workflow);
  if(workflow.v2RecoveryMigrationVersion===2)return migrateSectionTenLineage(workflow);
  const draft=workflow.v2ClientDraft;
  const capturedAt=workflow.clientUpdatedAt??draft?.generatedAt??workflow.v2ConfirmedAt??workflow.assessmentUpdatedAt??"";
  const identity=draft?sectionTenDraftIdentity(draft):workflow.v2ClientDraftIdentity;
  const manualFieldKeys=draft
    ? workflow.v2ClientDraftManualEditFieldKeys??inferSectionTenManualClientFields(draft,workflow.v2ConfirmedAssessment,workflow.v2ConfirmedAt)
    : workflow.v2ClientDraftManualEditFieldKeys;
  let migrated:SectionTenWorkflow={
    ...workflow,
    v2RecoveryMigrationVersion:2,
    v2WorkingSourceSnapshot:workflow.v2WorkingSourceSnapshot??workflow.v2SourceSnapshot,
    v2ClientDraftIdentity:identity,
    v2ClientDraftUpdatedAt:workflow.v2ClientDraftUpdatedAt??workflow.clientUpdatedAt??draft?.generatedAt,
    v2ClientDraftManualEditFieldKeys:manualFieldKeys,
    v2ClientDraftRequiresRecoveryReview:Boolean(draft&&!workflow.v2ClientDraftApproved),
  };
  if(draft){
    migrated=preserveSectionTenClientDraft(
      migrated,
      "Recovery snapshot before Section 10 draft-preservation repair",
      capturedAt,
      workflow.v2ConfirmedBy,
    );
  }
  return migrateSectionTenLineage(migrated);
}

export const markSectionTenClientFieldEdited=(keys:string[]|undefined,key:string)=>
  [...new Set([...(keys??[]),key])];

export function confirmSectionTenV2Assessment(
  workflow:SectionTenWorkflow,
  assessment:SectionTenAssessmentV2,
  workingSnapshot:SectionTenSourceSnapshot,
  confirmedAt:string,
  confirmedBy="Demo Operator",
):SectionTenWorkflow{
  const migrated=migrateSectionTenLineage(workflow),confirmation=makeSectionTenConfirmationLineage(assessment,workingSnapshot,confirmedAt,confirmedBy),previous=migrated.sectionTenLineage?.confirmation,sameConfirmation=previous?.id===confirmation.id,history=migrated.sectionTenLineage?.confirmationHistory??[];
  return migrateSectionTenLineage({
    ...migrated,
    redesignVersion:2,
    v2ConfirmedAssessment:{...assessment,updatedAt:confirmedAt,updatedBy:confirmedBy},
    v2ConfirmedAt:confirmedAt,
    v2ConfirmedBy:confirmedBy,
    v2ClientDraftApproved:sameConfirmation?migrated.v2ClientDraftApproved:false,
    v2ClientDraftApprovedAt:sameConfirmation?migrated.v2ClientDraftApprovedAt:undefined,
    v2ClientDraftRequiresRecoveryReview:Boolean(migrated.v2ClientDraft&&!sameConfirmation),
    assessmentUpdatedAt:confirmedAt,
    v2SourceSnapshot:workingSnapshot,
    sectionTenLineage:{
      ...(migrated.sectionTenLineage??{version:1,confirmationHistory:[],approvalHistory:[],completionHistory:[],reconciliations:[]}),
      confirmation,
      confirmationHistory:history.some(item=>item.id===confirmation.id)?history:[...history,confirmation],
    },
  });
}

export const PRESERVED_WORDING_RECONCILIATION_LABEL="Use preserved wording with current assessment";
export const PRESERVED_WORDING_RECONCILIATION_EXPLANATION="This creates a new current draft using exactly the same wording. It does not approve or complete the section.";

export function usePreservedSectionTenWordingWithCurrentAssessment(
  workflow:SectionTenWorkflow,
  recordedAt:string,
  recordedBy="Demo Operator",
):SectionTenWorkflow{
  const migrated=migrateSectionTenLineage(workflow),draft=migrated.v2ClientDraft,confirmation=migrated.sectionTenLineage?.confirmation;
  if(!draft||!confirmation||!migrated.v2ConfirmedAt)return migrated;
  const preserved=preserveSectionTenClientDraft(migrated,"Preserved wording linked to the current confirmed assessment",recordedAt,recordedBy);
  const linkedDraft:SectionTenClientDraftV2={
    ...cloneDraft(draft),
    generatedAt:recordedAt,
    sourceAssessmentConfirmedAt:migrated.v2ConfirmedAt,
    sourceAssessmentConfirmationId:confirmation.id,
  };
  const linked=linkSavedSectionTenDraft(preserved,linkedDraft),fromDraftId=migrated.sectionTenLineage?.currentDraftId??sectionTenDraftIdentity(draft),toDraftId=linked.sectionTenLineage!.currentDraftId!;
  const reconciliation={
    id:`section10-reconciliation-${checksum(`${fromDraftId}|${toDraftId}|${recordedAt}`)}`,
    fromDraftId,
    toDraftId,
    targetConfirmationId:confirmation.id,
    recordedAt,
    recordedBy,
    wordingPreserved:true as const,
  };
  return{
    ...linked,
    v2ClientDraftApproved:false,
    v2ClientDraftApprovedAt:undefined,
    v2ClientDraftRequiresRecoveryReview:false,
    v2CompletedAt:undefined,
    v2CompletedBy:undefined,
    sectionTenLineage:{
      ...linked.sectionTenLineage!,
      approvedDraftId:undefined,
      completedDraftId:undefined,
      reconciliations:linked.sectionTenLineage!.reconciliations.some(item=>item.id===reconciliation.id)
        ?linked.sectionTenLineage!.reconciliations
        :[...linked.sectionTenLineage!.reconciliations,reconciliation],
    },
  };
}

export function refreshSectionTenGeneratedFields(
  current:SectionTenClientDraftV2,
  generated:SectionTenClientDraftV2,
  manualFieldKeys:string[],
):SectionTenClientDraftV2{
  const manual=new Set(manualFieldKeys);
  const select=(key:string,currentValue:string,generatedValue:string)=>manual.has(key)?currentValue:generatedValue;
  return{
    ...generated,
    overview:select(SECTION_TEN_CLIENT_FIELDS.overview,current.overview,generated.overview),
    deliveryOptions:select(SECTION_TEN_CLIENT_FIELDS.deliveryOptions,current.deliveryOptions,generated.deliveryOptions),
    nextDecision:select(SECTION_TEN_CLIENT_FIELDS.nextDecision,current.nextDecision,generated.nextDecision),
    evidenceLimitations:select(SECTION_TEN_CLIENT_FIELDS.evidenceLimitations,current.evidenceLimitations,generated.evidenceLimitations),
    workAreas:generated.workAreas.map(generatedArea=>{
      const currentArea=current.workAreas.find(item=>item.id===generatedArea.id);
      if(!currentArea)return generatedArea;
      const max=Math.max(currentArea.points.length,generatedArea.points.length),points:string[]=[];
      for(let index=0;index<max;index+=1){
        const key=sectionTenPointKey(generatedArea.id,index),existing=currentArea.points[index],replacement=generatedArea.points[index];
        if(manual.has(key)&&existing!==undefined)points.push(existing);
        else if(replacement!==undefined)points.push(replacement);
      }
      return{
        ...generatedArea,
        summary:select(sectionTenAreaSummaryKey(generatedArea.id),currentArea.summary,generatedArea.summary),
        points,
      };
    }),
  };
}
